/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, GuardResult, RedirectCommand, Router,
  RouterStateSnapshot } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Tree } from '../shared/models/tree.model';
import { TreeService } from '../shared/services/tree.service';
import { AuthService } from '../shared/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class TreeGuard  implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(private router: Router, private treeService: TreeService,
    private authService: AuthService) { }

  /**
   * Czy wolno wejść do drzewa (migracja 071). Trzy stopnie widoczności:
   * public - każdy, users - każdy zalogowany, private - tylko uczestnik.
   * Uczestnik wchodzi zawsze. Prawdziwą bramkę trzyma backend, tu chodzi
   * o to, żeby nie pokazywać ekranu logowania komuś, kto już jest zalogowany.
   */
  private wolnoWejsc(tree: Tree): boolean {
    if (tree.membershiplevel > 0) {
      return true;
    }
    const widocznosc = tree.visibility
      ?? (tree.visiblepublic ? 'public' : tree.visiblelogon ? 'users' : 'private');

    return widocznosc === 'public'
      || (widocznosc === 'users' && this.authService.isLoggedIn());
  }

  /** Drzewo dla zalogowanych, a gość nie jest zalogowany - pokazujemy logowanie. */
  private prosicOLogowanie(tree: Tree): boolean {
    const widocznosc = tree.visibility
      ?? (tree.visiblepublic ? 'public' : tree.visiblelogon ? 'users' : 'private');

    return widocznosc === 'users' && !this.authService.isLoggedIn();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  /* canLoad usuniety 2026-08-28. Nie byl zarejestrowany na ZADNEJ trasie -
     obie tablice wpinaja TreeGuard wylacznie przez canActivate - wiec byl to
     martwy kod, w dodatku rozjezdzajacy sie z canActivate: dla drzewa
     prywatnego zwracal true, podczas gdy canActivate przekierowuje na
     /not-authorized. Sama regula zyje w canActivate ponizej. */

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<GuardResult> | GuardResult {
    /*      if (!this.authService.isLoggedIn) {
            // redirect to some view explaining what happened
            this.router.navigateByUrl('/notauthorized');
            return false;
          } else {
            return true;
          }*/

    return this.treeService.setCurrentTreeByUrl(route.children[0].params['tree'])
      .pipe(takeUntil(this.ngUnsubscribe), map(result => {
        route.data = { ...route.data, tree: result };
        if (result instanceof Tree) {
          const ekranLogowania = '/tree/' + result.url + '/login';
          const gdzieJestesmy = state.url.split('?')[0];
          if (this.wolnoWejsc(result)) {
            // Ktoś wszedł na ekran logowania z zakładki, choć drzewo i tak
            // może oglądać - nie ma go po co pytać o logowanie.
            return gdzieJestesmy === ekranLogowania
              ? new RedirectCommand(this.router.createUrlTree(['/tree', result.url]))
              : true;
          }
          else if (this.prosicOLogowanie(result)) {
            // Porównujemy same ścieżki - adres ekranu logowania niesie ?from=...,
            // więc porównanie z pełnym state.url zapętlałoby przekierowanie.
            if (gdzieJestesmy === ekranLogowania) {
              return true;
            }
            // Dokąd wrócić po zalogowaniu; ekran logowania przekazuje to dalej.
            const target = this.router.createUrlTree([ekranLogowania],
              { queryParams: { from: state.url } });

            return new RedirectCommand(target);
          }
          else {
            // trasa nazywa sie 'not-authorized' - stara nazwa nie pasowala
            // do zadnej i router konczyl bledem NG04002 na pustej stronie
            const target = this.router.createUrlTree(['/not-authorized'],
              { queryParams: { reason: 'private', from: state.url } });
            // skipLocationChange: adres prywatnego drzewa zostaje w pasku,
            // wiec po zalogowaniu wystarczy odswiezyc strone
            return new RedirectCommand(target, { skipLocationChange: true });
          }
        } else {
          return false;
        }
      }));
  }
}
